import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface TeacherWorkloadProps {
  domain: string;
}

interface Teacher {
  id: string;
  name: string;
  employeeId: string;
  department?: string;
  subjects?: string[];
}

interface TeacherSchedule {
  teacherId: string;
  teacherName: string;
  day: string;
  periods: {
    classId: string;
    className: string;
    subject: string;
    startTime: string;
    endTime: string;
    duration: number;
  }[];
}

interface WorkloadData {
  teacherId: string;
  teacherName: string;
  employeeId: string;
  department: string;
  totalPeriods: number;
  totalHours: number;
  dayWiseLoad: { [day: string]: number };
  subjects: string[];
  classes: string[];
  occupancyPercent: number;
}

const TeacherWorkload: React.FC<TeacherWorkloadProps> = ({ domain }) => {
  const [workloadData, setWorkloadData] = useState<WorkloadData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  const [sortBy, setSortBy] = useState<'name' | 'workload' | 'occupancy'>('workload');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MAX_PERIODS_PER_DAY = 8;
  const MAX_PERIODS_PER_WEEK = MAX_PERIODS_PER_DAY * 6;

  useEffect(() => {
    fetchWorkloadData();
  }, [domain]);

  const fetchWorkloadData = async () => {
    if (!domain) return;
    setLoading(true);

    try {
      // Fetch teachers
      const teachersRef = collection(db, `school-users/${domain}/teachers`);
      const teachersSnapshot = await getDocs(teachersRef);
      const teachers: Teacher[] = [];
      teachersSnapshot.forEach(doc => {
        teachers.push({ id: doc.id, ...doc.data() } as Teacher);
      });

      // Fetch teacher schedules
      const schedulesRef = collection(db, `school-users/${domain}/teacher-schedules`);
      const schedulesSnapshot = await getDocs(schedulesRef);
      const schedules: TeacherSchedule[] = [];
      schedulesSnapshot.forEach(doc => {
        schedules.push(doc.data() as TeacherSchedule);
      });

      // Fetch timetables to calculate workload from class timetables
      const timetablesRef = collection(db, `school-users/${domain}/timetables`);
      const timetablesSnapshot = await getDocs(timetablesRef);

      // Build workload map from timetables
      const workloadMap: Map<string, WorkloadData> = new Map();

      timetablesSnapshot.forEach(doc => {
        const data = doc.data();
        const periods = data.periods || [];
        const day = data.day;
        const className = data.className;

        periods.forEach((period: any) => {
          if (!period.isBreak && period.teacherId) {
            const teacherId = period.teacherId;
            const teacherName = period.teacherName || 'Unknown';

            if (!workloadMap.has(teacherId)) {
              workloadMap.set(teacherId, {
                teacherId,
                teacherName,
                employeeId: teacherId,
                department: 'General',
                totalPeriods: 0,
                totalHours: 0,
                dayWiseLoad: {},
                subjects: [],
                classes: [],
                occupancyPercent: 0,
              });
            }

            const workload = workloadMap.get(teacherId)!;
            workload.totalPeriods += 1;
            workload.totalHours += (period.duration || 45) / 60;

            if (!workload.dayWiseLoad[day]) {
              workload.dayWiseLoad[day] = 0;
            }
            workload.dayWiseLoad[day] += 1;

            if (period.subject && !workload.subjects.includes(period.subject)) {
              workload.subjects.push(period.subject);
            }

            if (className && !workload.classes.includes(className)) {
              workload.classes.push(className);
            }
          }
        });
      });

      // Merge with teacher data and calculate occupancy
      workloadMap.forEach(workload => {
        const teacher = teachers.find(t => t.employeeId === workload.teacherId || t.id === workload.teacherId);
        if (teacher) {
          workload.teacherName = teacher.name || workload.teacherName;
          workload.department = teacher.department || 'General';
        }
        workload.occupancyPercent = Math.round((workload.totalPeriods / MAX_PERIODS_PER_WEEK) * 100);
      });

      setWorkloadData(Array.from(workloadMap.values()));
    } catch (error) {
      console.error('Error fetching workload data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getOccupancyColor = (percent: number): string => {
    if (percent >= 90) return '#dc3545'; // Overloaded
    if (percent >= 70) return '#ffc107'; // High
    if (percent >= 50) return '#28a745'; // Optimal
    return '#17a2b8'; // Low
  };

  const getOccupancyLabel = (percent: number): string => {
    if (percent >= 90) return 'Overloaded';
    if (percent >= 70) return 'High';
    if (percent >= 50) return 'Optimal';
    return 'Under-utilized';
  };

  const departments = ['all', ...new Set(workloadData.map(w => w.department))];

  const filteredData = workloadData
    .filter(w => filterDepartment === 'all' || w.department === filterDepartment)
    .sort((a, b) => {
      if (sortBy === 'name') return a.teacherName.localeCompare(b.teacherName);
      if (sortBy === 'workload') return b.totalPeriods - a.totalPeriods;
      return b.occupancyPercent - a.occupancyPercent;
    });

  const avgOccupancy = workloadData.length > 0
    ? Math.round(workloadData.reduce((sum, w) => sum + w.occupancyPercent, 0) / workloadData.length)
    : 0;

  const overloadedCount = workloadData.filter(w => w.occupancyPercent >= 90).length;
  const underutilizedCount = workloadData.filter(w => w.occupancyPercent < 50).length;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                  <i className="bi bi-people-fill text-primary fs-4"></i>
                </div>
                <div>
                  <div className="text-muted small">Total Teachers</div>
                  <div className="fs-4 fw-bold">{workloadData.length}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                  <i className="bi bi-speedometer2 text-success fs-4"></i>
                </div>
                <div>
                  <div className="text-muted small">Avg Occupancy</div>
                  <div className="fs-4 fw-bold">{avgOccupancy}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-danger bg-opacity-10 p-3 me-3">
                  <i className="bi bi-exclamation-triangle-fill text-danger fs-4"></i>
                </div>
                <div>
                  <div className="text-muted small">Overloaded</div>
                  <div className="fs-4 fw-bold">{overloadedCount}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                  <i className="bi bi-arrow-down-circle-fill text-info fs-4"></i>
                </div>
                <div>
                  <div className="text-muted small">Under-utilized</div>
                  <div className="fs-4 fw-bold">{underutilizedCount}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-3">
              <label className="form-label small text-muted">Department</label>
              <select
                className="form-select"
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>
                    {dept === 'all' ? 'All Departments' : dept}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted">Sort By</label>
              <select
                className="form-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'workload' | 'occupancy')}
              >
                <option value="workload">Workload (High to Low)</option>
                <option value="occupancy">Occupancy %</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted">View</label>
              <div className="btn-group w-100">
                <button
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('cards')}
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('table')}
                >
                  <i className="bi bi-table"></i>
                </button>
              </div>
            </div>
            <div className="col-md-3 text-end">
              <label className="form-label small text-muted d-block">&nbsp;</label>
              <button className="btn btn-outline-primary" onClick={fetchWorkloadData}>
                <i className="bi bi-arrow-clockwise me-2"></i>Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Occupancy Legend */}
      <div className="d-flex gap-4 mb-4 flex-wrap">
        <div className="d-flex align-items-center">
          <div className="rounded-circle me-2" style={{ width: 12, height: 12, backgroundColor: '#dc3545' }}></div>
          <small>Overloaded (≥90%)</small>
        </div>
        <div className="d-flex align-items-center">
          <div className="rounded-circle me-2" style={{ width: 12, height: 12, backgroundColor: '#ffc107' }}></div>
          <small>High (70-89%)</small>
        </div>
        <div className="d-flex align-items-center">
          <div className="rounded-circle me-2" style={{ width: 12, height: 12, backgroundColor: '#28a745' }}></div>
          <small>Optimal (50-69%)</small>
        </div>
        <div className="d-flex align-items-center">
          <div className="rounded-circle me-2" style={{ width: 12, height: 12, backgroundColor: '#17a2b8' }}></div>
          <small>Under-utilized (&lt;50%)</small>
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          No teacher workload data found. Please ensure timetables are uploaded with teacher information.
        </div>
      ) : viewMode === 'cards' ? (
        /* Cards View */
        <div className="row g-3">
          {filteredData.map(teacher => (
            <div key={teacher.teacherId} className="col-md-6 col-lg-4">
              <div
                className="card border-0 shadow-sm h-100"
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedTeacher(selectedTeacher === teacher.teacherId ? null : teacher.teacherId)}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 className="fw-bold mb-1">{teacher.teacherName}</h6>
                      <small className="text-muted">{teacher.employeeId}</small>
                    </div>
                    <span
                      className="badge"
                      style={{ backgroundColor: getOccupancyColor(teacher.occupancyPercent) }}
                    >
                      {getOccupancyLabel(teacher.occupancyPercent)}
                    </span>
                  </div>

                  {/* Occupancy Bar */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between small mb-1">
                      <span>Occupancy</span>
                      <span className="fw-bold">{teacher.occupancyPercent}%</span>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className="progress-bar"
                        style={{
                          width: `${teacher.occupancyPercent}%`,
                          backgroundColor: getOccupancyColor(teacher.occupancyPercent)
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="row g-2 small">
                    <div className="col-6">
                      <div className="text-muted">Total Periods</div>
                      <div className="fw-bold">{teacher.totalPeriods}/week</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Total Hours</div>
                      <div className="fw-bold">{teacher.totalHours.toFixed(1)}h/week</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Classes</div>
                      <div className="fw-bold">{teacher.classes.length}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Subjects</div>
                      <div className="fw-bold">{teacher.subjects.length}</div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedTeacher === teacher.teacherId && (
                    <div className="mt-3 pt-3 border-top">
                      <h6 className="small fw-bold mb-2">Day-wise Load</h6>
                      <div className="d-flex flex-wrap gap-1 mb-3">
                        {days.map(day => (
                          <span key={day} className="badge bg-light text-dark">
                            {day.slice(0, 3)}: {teacher.dayWiseLoad[day] || 0}
                          </span>
                        ))}
                      </div>

                      <h6 className="small fw-bold mb-2">Subjects</h6>
                      <div className="d-flex flex-wrap gap-1 mb-3">
                        {teacher.subjects.map(subject => (
                          <span key={subject} className="badge bg-primary bg-opacity-10 text-primary">
                            {subject}
                          </span>
                        ))}
                      </div>

                      <h6 className="small fw-bold mb-2">Classes</h6>
                      <div className="d-flex flex-wrap gap-1">
                        {teacher.classes.map(cls => (
                          <span key={cls} className="badge bg-secondary bg-opacity-10 text-secondary">
                            {cls}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Teacher</th>
                  <th>Employee ID</th>
                  <th>Department</th>
                  <th className="text-center">Periods/Week</th>
                  <th className="text-center">Hours/Week</th>
                  <th className="text-center">Classes</th>
                  <th>Occupancy</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map(teacher => (
                  <tr key={teacher.teacherId}>
                    <td className="fw-semibold">{teacher.teacherName}</td>
                    <td><code>{teacher.employeeId}</code></td>
                    <td>{teacher.department}</td>
                    <td className="text-center">{teacher.totalPeriods}</td>
                    <td className="text-center">{teacher.totalHours.toFixed(1)}</td>
                    <td className="text-center">{teacher.classes.length}</td>
                    <td style={{ width: '150px' }}>
                      <div className="d-flex align-items-center">
                        <div className="progress flex-grow-1 me-2" style={{ height: '6px' }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${teacher.occupancyPercent}%`,
                              backgroundColor: getOccupancyColor(teacher.occupancyPercent)
                            }}
                          ></div>
                        </div>
                        <small className="fw-bold">{teacher.occupancyPercent}%</small>
                      </div>
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{ backgroundColor: getOccupancyColor(teacher.occupancyPercent) }}
                      >
                        {getOccupancyLabel(teacher.occupancyPercent)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherWorkload;
